import { defineStore } from "pinia";
import { DeliveryEntry } from "./entity";
import { DeliveryEntryList } from "./list.gen";
import type {
  DeliveryEntryStore,
  DeliveryEntryStoreState,
} from "./store.gen.d";
import { analytics, auth } from "@/firebase";
import { type JSONData } from "../../base_classes/dto.d";
import { Pagination } from "../../base_classes/pagination";
import { Query } from "../../base_classes/query";
import { paginationAndQueriesToCacheKey } from "../../base_classes/dto_list";

/// ///////////////////////////////////////////////////////////////////////////////////////////////////
/// WARNING! THIS FILE IS AUTOMATICALLY GENERATED, ANY CHANGES WILL BE LOST ON NEXT GENERATION
/// IF YOU WANT TO EDIT THIS FILE, REMOVE THE .gen. FROM THE FILENAME. THE FILE WILL THEN BE IGNORED.
/// ///////////////////////////////////////////////////////////////////////////////////////////////////

const deliveryEntryList = new DeliveryEntryList();
export const useDeliveryEntryStore = defineStore("deliveryEntryStore", {
  state: (): DeliveryEntryStoreState => ({
    activeID: null,
    cache: {},
    list: [] as DeliveryEntry[],
    retrievalTimestamp: 0,
    updateKey: 0,
    storeUpdateTimeout: null,
  }),
  actions: {
    initializeStore(): void {
      this.convertList();
    },
    emitFormUpdateEvent(id: string): void {
      window.dispatchEvent(
        new CustomEvent("form-update-DeliveryEntry", { detail: { id } }),
      );
    },
    emitStoreUpdateEvent(): void {
      // Clear existing timeout
      if (this.storeUpdateTimeout !== null) {
        clearTimeout(this.storeUpdateTimeout);
      }

      // Set new timeout
      this.storeUpdateTimeout = setTimeout(() => {
        window.dispatchEvent(new CustomEvent("store.update.DeliveryEntry"));
        this.storeUpdateTimeout = null;
      }, 1500);
    },
    async activate(deliveryEntry: DeliveryEntry): Promise<void> {
      // Set active
      await deliveryEntry
        .activate(auth.firebaseUser?.uid)
        .then(() => {
          this.activeID = deliveryEntry.id;
        })
        .catch((error) => {
          console.error(error);
          this.activeID = null;
        });
    },
    add(deliveryEntry: DeliveryEntry): void {
      const id = deliveryEntry.id;
      if (typeof id === "string" && !this.contains(id)) {
        this.list.push(deliveryEntry);

        // Set active
        if (this.active === null) {
          this.activeID = deliveryEntry.id;
        }
      } else {
        this.reinsert(deliveryEntry);
      }
      this.updateKey += 1;
    },
    convertList(): void {
      this.list = (this.list as DeliveryEntry[]).map((x: any) => {
        const deliveryEntry = new DeliveryEntry({});
        deliveryEntry.set(x as JSONData);
        return deliveryEntry;
      });
    },
    async delete(deliveryEntry: DeliveryEntry): Promise<void> {
      await deliveryEntry
        .delete()
        .then(() => {
          this.remove(deliveryEntry);
        })
        .finally(() => {
          this.emitStoreUpdateEvent();
        });
    },
    async deleteById(id: string): Promise<void> {
      const deliveryEntry = (this.list as DeliveryEntry[]).find(
        (x: DeliveryEntry) => x.id === id,
      );
      if (deliveryEntry !== undefined) {
        await this.delete(deliveryEntry);
      } else {
        throw new Error("Could not find DeliveryEntry with id: " + id);
      }
    },
    contains(id: string): boolean {
      return (this.list as DeliveryEntry[]).some(
        (x: DeliveryEntry) => x.id === id,
      );
    },
    get(id: string): DeliveryEntry | null {
      return (
        (this.list as DeliveryEntry[]).find(
          (x: DeliveryEntry) => x.id === id,
        ) || null
      );
    },
    reinsert(deliveryEntry: DeliveryEntry): void {
      const index = (this.list as DeliveryEntry[]).findIndex(
        (x: DeliveryEntry) => x.id === deliveryEntry.id,
      );
      if (index !== -1) {
        this.list = [
          ...this.list.slice(0, index),
          deliveryEntry,
          ...this.list.slice(index + 1),
        ];
      } else {
        this.add(deliveryEntry);
      }
      this.updateKey += 1;
    },
    remove({ id }: DeliveryEntry): void {
      const index = (this.list as DeliveryEntry[]).findIndex(
        (x: DeliveryEntry) => x.id === id,
      );
      if (index !== -1) {
        this.list = [
          ...this.list.slice(0, index),
          ...this.list.slice(index + 1),
        ];
      }
      this.updateKey += 1;
    },
    async retrieve(id: string, force = false): Promise<DeliveryEntry> {
      // Check if a refresh is required
      if (!force && !this.refreshRequired) {
        const deliveryEntry = this.get(id);
        if (deliveryEntry !== null) {
          return deliveryEntry;
        }
      }

      // Retrieve deliveryEntry
      const deliveryEntry = new DeliveryEntry({});

      // Get deliveryEntry
      await deliveryEntry.get([id]).then(async (entity) => {
        this.reinsert(entity);

        // Activate deliveryEntry
        await this.activate(entity);
      });

      return deliveryEntry;
    },
    async retrieveAll(force = false): Promise<{
      active: string | null;
      items: DeliveryEntry[];
    }> {
      // Check if a refresh is required
      if (!force && !this.refreshRequired) {
        return {
          active: this.activeID,
          items: this.list as DeliveryEntry[],
        };
      }

      // Clear list
      while (this.list.length > 0) {
        this.list.pop();
      }

      // Retrieve list
      await deliveryEntryList.getAll().then(async (list) => {
        list.forEach((deliveryEntry) => {
          this.reinsert(deliveryEntry);
        });

        // Activate deliveryEntry
        if (list.length > 0) {
          if (deliveryEntryList.active !== null) {
            this.activeID = deliveryEntryList.active;
          } else {
            await this.activate(list[0]);
          }
        }
      });

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      // Set user property
      if (this.activeID !== null) {
        analytics.setUserProperty("delivery_entry_id", this.activeID);
      }

      return {
        active: this.activeID,
        items: this.list as DeliveryEntry[],
      };
    },
    async retrievePaginated(
      pagination: Pagination,
      force = false,
    ): Promise<{
      pagination: Pagination;
      items: DeliveryEntry[];
    }> {
      // Check pagination
      let newPagination = new Pagination(pagination).deepCopy();

      // Get cachekey
      const cacheKey = pagination.toCacheKey();

      // Check if a refresh is required
      if (!force && this.cache[cacheKey]) {
        const cached = this.cache[cacheKey];
        return {
          pagination: cached.pagination.deepCopy(),
          items: cached.ids.map((id: string) => this.get(id) as DeliveryEntry),
        };
      }

      // Retrieve list
      const items = await deliveryEntryList
        .getPaginated([], pagination)
        .then(async ({ items, pagination }) => {
          items.forEach((deliveryEntry) => {
            this.reinsert(deliveryEntry);
          });

          // Set new pagination
          newPagination = pagination.deepCopy();

          return items;
        });

      // Set cache
      this.cache[cacheKey] = {
        ids: items.map((x: DeliveryEntry) => x.id),
        pagination: newPagination,
        retrievalTimestamp: Date.now(),
      };

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      return {
        pagination: newPagination,
        items,
      };
    },
    async query(
      queries: Query[],
      pagination: Pagination,
      force = false,
    ): Promise<{
      pagination: Pagination;
      items: DeliveryEntry[];
    }> {
      // Check pagination
      let newPagination = new Pagination(pagination).deepCopy();

      // Get cachekey
      const cacheKey = paginationAndQueriesToCacheKey(pagination, queries);

      // Check if a refresh is required
      if (!force && this.cache[cacheKey]) {
        const cached = this.cache[cacheKey];
        return {
          pagination: cached.pagination.deepCopy(),
          items: cached.ids.map((id: string) => this.get(id) as DeliveryEntry),
        };
      }

      // Retrieve list
      const items = await deliveryEntryList
        .query(queries, pagination)
        .then(async ({ items, pagination }) => {
          items.forEach((deliveryEntry) => {
            this.reinsert(deliveryEntry);
          });

          // Set new pagination
          newPagination = pagination.deepCopy();

          return items;
        });

      // Set cache
      this.cache[cacheKey] = {
        ids: items.map((x: DeliveryEntry) => x.id),
        pagination: newPagination.deepCopy(),
        retrievalTimestamp: Date.now(),
      };

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      return {
        pagination: newPagination,
        items: items,
      };
    },
    async search(
      query: string,
      pagination: Pagination,
      force = false,
    ): Promise<{
      pagination: Pagination;
      items: DeliveryEntry[];
    }> {
      // Check pagination
      let newPagination = new Pagination(pagination).deepCopy();

      // Get cachekey
      const cacheKey =
        query.replaceAll(" ", "_") + "-" + pagination.toCacheKey();

      // Check if a refresh is required
      if (!force && this.cache[cacheKey]) {
        const cached = this.cache[cacheKey];
        return {
          items: cached.ids.map((id: string) => this.get(id) as DeliveryEntry),
          pagination: cached.pagination.deepCopy(),
        };
      }

      // Retrieve list
      const items = await deliveryEntryList
        .search(query, pagination)
        .then(async ({ items, pagination }) => {
          items.forEach((deliveryEntry) => {
            this.reinsert(deliveryEntry);
          });

          // Set new pagination
          newPagination = pagination.deepCopy();

          return items;
        });

      // Set cache
      this.cache[cacheKey] = {
        ids: items.map((x: DeliveryEntry) => x.id),
        pagination: newPagination.deepCopy(),
        retrievalTimestamp: Date.now(),
      };

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      return {
        items: items,
        pagination: newPagination,
      };
    },
    async update(deliveryEntry: DeliveryEntry): Promise<string> {
      // Update deliveryEntry
      await deliveryEntry.update();

      // Reinsert deliveryEntry
      this.reinsert(deliveryEntry);

      // Activate deliveryEntry
      if (this.active === null) {
        await this.activate(deliveryEntry);
      }

      this.emitStoreUpdateEvent();

      return deliveryEntry.id;
    },
  },
  getters: {
    active(): DeliveryEntry | null {
      if (this.activeID === null) {
        return null;
      }

      const deliveryEntry = (this.list as DeliveryEntry[]).find(
        (x: DeliveryEntry) => x.id === this.activeID,
      );
      if (deliveryEntry === undefined) {
        return null;
      }

      return deliveryEntry;
    },
    refreshRequired(): boolean {
      return (
        this.retrievalTimestamp === 0 ||
        this.retrievalTimestamp < Date.now() - 30 * 60000 ||
        this.list.length === 0
      );
    },
  },
});

// Export a function to create a new instance of the store
export function createDeliveryEntryStore(): DeliveryEntryStore {
  const store = useDeliveryEntryStore();
  // Run the initializeStore action when the store is created
  store.initializeStore();
  return store as unknown as DeliveryEntryStore; // TODO: Fix this
}
